import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, GitCompare } from 'lucide-react';
import { diagnosticsAPI, entitiesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * Intercompany Position diagnostic (Fix 2b).
 *
 * Pick two parties; see every entry composing their position (including
 * settlements and reimbursed loans) and any candidate bank transactions near a
 * date/amount that could be an unrecorded settlement.
 */
export function IntercompanyPositionPanel() {
  var [entities, setEntities] = useState([]);
  var [partyA, setPartyA] = useState('piotr_kacki');
  var [partyB, setPartyB] = useState('sci_113');
  var [aroundDate, setAroundDate] = useState('2026-07-02');
  var [amount, setAmount] = useState('40000');
  var [loading, setLoading] = useState(false);
  var [result, setResult] = useState(null);

  useEffect(function () {
    entitiesAPI.getAll().then(function (res) {
      var list = res.data || [];
      list.sort(function (a, b) { return (a.name || '').localeCompare(b.name || ''); });
      setEntities(list);
    }).catch(function () {});
  }, []);

  var run = async function () {
    if (!partyA || !partyB) return;
    setLoading(true);
    setResult(null);
    try {
      var params = { party_a: partyA, party_b: partyB };
      if (aroundDate) params.around_date = aroundDate;
      if (amount) params.amount = amount;
      var res = await diagnosticsAPI.intercompanyPosition(params);
      setResult(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <GitCompare className="h-4 w-4" />
          Intercompany Position
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <select className="text-sm border rounded px-2 py-1.5 bg-background" value={partyA}
            onChange={function (e) { setPartyA(e.target.value); }}>
            <option value="">Party A...</option>
            {entities.map(function (ent) { return <option key={ent.id} value={ent.id}>{ent.name}</option>; })}
          </select>
          <select className="text-sm border rounded px-2 py-1.5 bg-background" value={partyB}
            onChange={function (e) { setPartyB(e.target.value); }}>
            <option value="">Party B...</option>
            {entities.map(function (ent) { return <option key={ent.id} value={ent.id}>{ent.name}</option>; })}
          </select>
          <input className="text-sm border rounded px-2 py-1.5 bg-background" placeholder="around date YYYY-MM-DD"
            value={aroundDate} onChange={function (e) { setAroundDate(e.target.value); }} />
          <input className="text-sm border rounded px-2 py-1.5 bg-background" placeholder="approx amount"
            value={amount} onChange={function (e) { setAmount(e.target.value); }} />
        </div>
        <Button size="sm" onClick={run} disabled={loading || !partyA || !partyB}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run'}
        </Button>

        {result && (
          <div className="space-y-4 text-sm">
            <div className="bg-muted/50 rounded p-3 font-mono text-xs">
              <div>{result.party_a.name} ({result.party_a.id}) vs {result.party_b.name} ({result.party_b.id})</div>
              <div className="mt-1">Net position for {result.party_a.name}:{' '}
                {Object.keys(result.net_position_for_party_a || {}).length === 0
                  ? '0'
                  : Object.entries(result.net_position_for_party_a).map(function (e) {
                    return <span key={e[0]} className="mr-2">{e[0]} {e[1]}</span>;
                  })}
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-1">Entries ({result.entry_count})</h4>
              <div className="overflow-x-auto">
                <table className="text-xs w-full border-collapse">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1 pr-2">date</th>
                      <th className="py-1 pr-2">from → to</th>
                      <th className="py-1 pr-2">amount</th>
                      <th className="py-1 pr-2">source</th>
                      <th className="py-1 pr-2">type</th>
                      <th className="py-1 pr-2">reimb</th>
                      <th className="py-1 pr-2">counts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.entries.map(function (e) {
                      return (
                        <tr key={e.id} className="border-b border-border/30">
                          <td className="py-1 pr-2 font-mono">{(e.date || '').slice(0, 10)}</td>
                          <td className="py-1 pr-2 font-mono">{e.from_entity} → {e.to_entity}</td>
                          <td className="py-1 pr-2 font-mono">{e.native_amount} {e.currency}</td>
                          <td className="py-1 pr-2">{e.source || '-'}</td>
                          <td className="py-1 pr-2">{e.document_type || '-'}</td>
                          <td className="py-1 pr-2">{e.is_reimbursed ? 'yes' : 'no'}</td>
                          <td className="py-1 pr-2">{e.counts_toward_position
                            ? <Badge variant="default" className="text-[10px]">yes</Badge>
                            : <Badge variant="outline" className="text-[10px] text-muted-foreground">no</Badge>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-1 flex items-center gap-2">
                Candidate bank transactions ({result.candidate_transactions.length})
              </h4>
              {result.candidate_transactions.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  None found near {result.candidate_search.around_date} / {result.candidate_search.amount}.
                  If the payment is not here, it is not in the system: record it via the Intercompany page.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="text-xs w-full border-collapse">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-2">date</th>
                        <th className="py-1 pr-2">entity</th>
                        <th className="py-1 pr-2">amount</th>
                        <th className="py-1 pr-2">counterparty</th>
                        <th className="py-1 pr-2">resolves to</th>
                        <th className="py-1 pr-2">match?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.candidate_transactions.map(function (t) {
                        return (
                          <tr key={t.id} className="border-b border-border/30">
                            <td className="py-1 pr-2 font-mono">{(t.date || '').slice(0, 10)}</td>
                            <td className="py-1 pr-2 font-mono">{t.entity_id}</td>
                            <td className="py-1 pr-2 font-mono">{t.amount} {t.currency}</td>
                            <td className="py-1 pr-2 truncate max-w-[160px]">{t.counterparty || t.description || '-'}</td>
                            <td className="py-1 pr-2 font-mono">{t.resolves_to_counterparty || '-'}</td>
                            <td className="py-1 pr-2">{t.would_match_other_party
                              ? <Badge variant="default" className="text-[10px]">yes</Badge>
                              : <Badge variant="destructive" className="text-[10px]">no</Badge>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
